import { AddressAutocompleteInput } from '../common/AddressAutocompleteInput';
import React from 'react';
import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import type { Job } from '../../types';
import type {
  AppointmentTypeCode,
  DeliveryServiceCode,
  DistributorConnection,
} from '../../lib/distributorsApi';
import { SearchableSelect } from '../SearchableSelect';
import { APPOINTMENT_TYPES, DELIVERY_SERVICES, PO_NUMBER_MAX } from './abcConstants';
import SrsOrderDeliveryDetails from './SrsOrderDeliveryDetails';

export type OrderDetailsState = {
  contactPhone?: string;
  contactName?: string;
  contactEmail?: string;
  shipToNumber: string;
  branchNumber: string;
  fulfillment: 'delivery' | 'pickup';
  deliveryService: DeliveryServiceCode;
  requestedDate: string;
  appointmentType: AppointmentTypeCode;
  appointmentFrom: string;
  appointmentTo: string;
  appointmentInstructions: string;
  poNumber: string;
  jobId: number | null;
  deliveryAddress: { address: string; city: string; state: string; zip: string };
  notes: string;
};

export interface OrderDeliveryDetailsProps {
  isSrs?: boolean;
  poNumberMax?: number;
  connection: DistributorConnection;
  value: OrderDetailsState;
  onChange: (patch: Partial<OrderDetailsState>) => void;
  onJobSelected: (jobId: number | null) => void;
  jobs: Job[];
  jobsLoading: boolean;
}

/** Ship-to / branch / delivery / PO / job section of an API-mode order. */
export default function OrderDeliveryDetails({
  connection,
  value,
  onChange,
  onJobSelected,
  jobs,
  jobsLoading,
  isSrs,
  poNumberMax = PO_NUMBER_MAX,
}: OrderDeliveryDetailsProps) {
  if (isSrs) return <SrsOrderDeliveryDetails connection={connection} value={value} onChange={onChange} onJobSelected={onJobSelected} jobs={jobs} jobsLoading={jobsLoading} />;
  const shipTos = connection.ship_tos || [];
  const selectedShipTo = shipTos.find((shipTo) => shipTo.number === value.shipToNumber) || null;
  const branches = selectedShipTo?.branches || [];
  const selectedBranch = branches.find((branch) => branch.number === value.branchNumber) || null;
  const appointmentConfig = APPOINTMENT_TYPES.find((type) => type.value === value.appointmentType);

  const handleShipToChange = (_event: unknown, shipToNumber: string | null) => {
    if (!shipToNumber) return;
    const shipTo = shipTos.find((entry) => entry.number === shipToNumber);
    const home = shipTo?.branches.find((branch) => branch.homeBranch) || shipTo?.branches[0];
    onChange({ shipToNumber, branchNumber: home?.number || '' });
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <FormControl>
          <FormLabel>Ship-To account</FormLabel>
          <Select value={value.shipToNumber || null} onChange={handleShipToChange} placeholder="Select…">
            {shipTos.map((shipTo) => (
              <Option key={shipTo.number} value={shipTo.number}>
                {shipTo.number} — {shipTo.name || 'Unnamed'}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Branch</FormLabel>
          <Select
            value={value.branchNumber || null}
            onChange={(_event, branch) => branch && onChange({ branchNumber: branch })}
            placeholder="Select…"
            disabled={branches.length === 0}
          >
            {branches.map((branch) => (
              <Option key={branch.number} value={branch.number}>
                {branch.name || `Branch ${branch.number}`}{branch.homeBranch ? ' (home)' : ''}
              </Option>
            ))}
          </Select>
          {branches.length === 0 && (
            <FormHelperText>This Ship-To has no enrolled branches.</FormHelperText>
          )}
        </FormControl>
      </Box>

      <FormControl>
        <FormLabel>Fulfillment</FormLabel>
        <RadioGroup
          orientation="horizontal"
          value={value.fulfillment}
          onChange={(event) => {
            const fulfillment = event.target.value as 'delivery' | 'pickup';
            onChange({
              fulfillment,
              deliveryService: fulfillment === 'pickup' ? 'CPU' : 'COM',
            });
          }}
        >
          <Radio value="delivery" label="Delivery" />
          <Radio value="pickup" label="Pickup at branch" />
        </RadioGroup>
      </FormControl>

      {value.fulfillment === 'pickup' ? (
        <Alert color="neutral" variant="soft">
          Pickup at {selectedBranch?.name || 'the selected branch'}
          {selectedBranch ? ` (branch ${selectedBranch.number})` : ''}.
        </Alert>
      ) : (
        <FormControl>
          <FormLabel>Delivery service</FormLabel>
          <Select
            value={value.deliveryService}
            onChange={(_event, service) => service && onChange({ deliveryService: service })}
          >
            {DELIVERY_SERVICES.filter((service) => !service.isPickup).map((service) => (
              <Option key={service.value} value={service.value}>
                {service.label}
              </Option>
            ))}
          </Select>
          <FormHelperText>Service availability can vary by branch.</FormHelperText>
        </FormControl>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <FormControl>
          <FormLabel>Requested date</FormLabel>
          <Input
            type="date"
            value={value.requestedDate}
            onChange={(event) => onChange({ requestedDate: event.target.value })}
          />
          <FormHelperText>Leave blank for "TBD".</FormHelperText>
        </FormControl>
        <FormControl error={value.poNumber.length > poNumberMax}>
          <FormLabel>PO number</FormLabel>
          <Input
            value={value.poNumber}
            onChange={(event) => onChange({ poNumber: event.target.value })}
            placeholder="e.g. the job number"
          />
          <FormHelperText>
            {value.poNumber.length}/{poNumberMax}
            {value.poNumber.length > poNumberMax ? ' — too long for this distributor' : ''}
          </FormHelperText>
        </FormControl>
      </Box>

      {value.fulfillment === 'delivery' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
          <FormControl>
            <FormLabel>Time window</FormLabel>
            <Select
              value={value.appointmentType}
              onChange={(_event, type) => type && onChange({ appointmentType: type })}
            >
              {APPOINTMENT_TYPES.map((type) => (
                <Option key={type.value} value={type.value}>{type.label}</Option>
              ))}
            </Select>
          </FormControl>
          <FormControl error={!!appointmentConfig?.requiresFrom && !value.appointmentFrom}>
            <FormLabel>From</FormLabel>
            <Input
              type="time"
              value={value.appointmentFrom}
              onChange={(event) => onChange({ appointmentFrom: event.target.value })}
            />
          </FormControl>
          <FormControl error={!!appointmentConfig?.requiresTo && !value.appointmentTo}>
            <FormLabel>To</FormLabel>
            <Input
              type="time"
              value={value.appointmentTo}
              onChange={(event) => onChange({ appointmentTo: event.target.value })}
              disabled={!appointmentConfig?.requiresTo}
            />
          </FormControl>
        </Box>
      )}

      <SearchableSelect
        label="Job (optional — prefills the delivery address)"
        placeholder={jobsLoading ? 'Loading jobs…' : 'Search jobs…'}
        options={jobs.map((job) => ({
          value: job.id,
          label: `${job.job_number}${job.description ? ` — ${job.description.slice(0, 40)}` : ''}`,
          subtitle: `${job.address_1}, ${job.city}, ${job.state}`,
        }))}
        value={value.jobId}
        onChange={(jobId) => onJobSelected(jobId as number | null)}
      />

      {value.fulfillment === 'delivery' && (
        <FormControl>
          <FormLabel>Delivery address</FormLabel>
          <AddressAutocompleteInput
            value={value.deliveryAddress.address}
            sx={{ mb: 1 }}
            onChange={(address) => onChange({ deliveryAddress: { ...value.deliveryAddress, address } })}
            onAddressSelect={(parsed) => onChange({ deliveryAddress: { ...value.deliveryAddress, address: parsed.address_1, city: parsed.city, state: parsed.state, zip: parsed.zip_code } })}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 1 }}>
            <Input
              placeholder="City"
              value={value.deliveryAddress.city}
              onChange={(event) =>
                onChange({ deliveryAddress: { ...value.deliveryAddress, city: event.target.value } })
              }
            />
            <Input
              placeholder="State"
              value={value.deliveryAddress.state}
              onChange={(event) =>
                onChange({ deliveryAddress: { ...value.deliveryAddress, state: event.target.value } })
              }
            />
            <Input
              placeholder="ZIP"
              value={value.deliveryAddress.zip}
              onChange={(event) =>
                onChange({ deliveryAddress: { ...value.deliveryAddress, zip: event.target.value } })
              }
            />
          </Box>
          <FormHelperText>
            Leave blank to deliver to the Ship-To account's address on file.
          </FormHelperText>
        </FormControl>
      )}

      <Typography level="title-md">Jobsite contact</Typography>
      <Typography level="body-sm">The person ABC should contact about delivery or pickup.</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <FormControl required>
          <FormLabel>Jobsite contact name</FormLabel>
          <Input value={value.contactName || ''} onChange={event => onChange({ contactName: event.target.value })} slotProps={{ input: { maxLength: 255 } }} />
        </FormControl>
        <FormControl required>
          <FormLabel>Jobsite contact phone</FormLabel>
          <Input type="tel" value={value.contactPhone || ''} onChange={event => onChange({ contactPhone: event.target.value })} slotProps={{ input: { maxLength: 40 } }} />
        </FormControl>
      </Box>
      <FormControl required={value.fulfillment === 'pickup'}>
        <FormLabel>Jobsite contact email</FormLabel>
        <Input type="email" value={value.contactEmail || ''} onChange={event => onChange({ contactEmail: event.target.value })} />
        <FormHelperText>{value.fulfillment === 'pickup' ? 'Required for ABC pickup orders.' : 'Optional for delivery orders.'}</FormHelperText>
      </FormControl>
      {value.fulfillment === 'delivery' && (
        <FormControl error={value.appointmentInstructions.length > 255}>
          <FormLabel>Delivery instructions (optional)</FormLabel>
          <Textarea value={value.appointmentInstructions} onChange={event => onChange({ appointmentInstructions: event.target.value })} minRows={2} slotProps={{ textarea: { maxLength: 255 } }} />
          <FormHelperText>Sent to the branch’s delivery team. {value.appointmentInstructions.length}/255 characters.</FormHelperText>
        </FormControl>
      )}
      <FormControl>
        <FormLabel>Order notes (optional)</FormLabel>
        <Textarea
          value={value.notes}
          onChange={(event) => onChange({ notes: event.target.value })}
          minRows={2}
        />
        <FormHelperText>Sent to the branch as an order comment (255 chars max).</FormHelperText>
      </FormControl>

      {value.poNumber.length === 0 && (
        <Typography level="body-xs" color="neutral">
          A PO number is required before submitting to ABC Supply.
        </Typography>
      )}
    </Stack>
  );
}
